import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useContext, useEffect, useState, useMemo } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { StatusPill } from '../../../../v2/js/components/StatusPill';
import {
  ApiError,
  DegreeEnum,
  ProviderCredentialingProfessionalTraits,
  ProviderReference,
  ProviderService,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { FileUploader } from '../../../app/components/FileUploader';
import { FormFeedback } from '../../../app/components/FormFeedback';
import { FormFieldVisibility, FormLabel } from '../../../app/components/FormLabel';
import { FormTextareaField } from '../../../app/components/FormTextareaField';
import { FormTextField } from '../../../app/components/FormTextField';
import { SelectController } from '../../../app/components/SelectController';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { userQueryKeys } from '../../../app/query-keys-factories/user';
import { handleApiError } from '../../../app/utils/errorhandling';
import { generateShowFieldErrors } from '../../../utils/forms';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { ProviderFormContainer } from './ProviderFormContainer';

const Specialties = Object.keys(window.SPECIALTIES).map((key) => ({
  value: key,
  label: window.SPECIALTIES[key],
}));

const referenceSchema = z.object({
  id: z.string().uuid().optional(),
  full_name: z.string().min(1),
  email: z.string().min(1).email(),
  recommendation_letter__file: z
    .union([z.string().length(0), z.null(), z.string().url()])
    .optional(),
});

const editValidationSchema = z.object({
  npi: z
    .string({ required_error: 'NPI is required', invalid_type_error: 'NPI is required' }) // Required rule
    .refine((val) => /^\d{10}$/.test(val), {
      message: 'NPI must be exactly 10 numbers',
    }),
  degree: z.array(z.string()).min(1, 'Select at least one degree'),
  specialties: z.array(z.string()).min(1, 'Select at least one specialty'),
  professional_highlights: z.union([z.string().length(0), z.null(), z.string()]).optional(),
  cv: z
    .union([
      z.string().url(), // valid URL for uploaded file
      z.string().length(0), // empty string
      z.null(), // null value
    ])
    .optional(), // field itself is optional
  linkedin_link: z.union([z.string().length(0), z.null(), z.string().url()]),
  languages_spoken: z.array(z.string()),
  references: z.array(referenceSchema),
});

export const ProviderProfessionalTraitsForm = () => {
  useDocumentTitle('Provider Professional Traits');
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    reset,
    formState: { isDirty, errors, isSubmitSuccessful },
    setValue,
  } = useForm({ resolver: zodResolver(editValidationSchema) });
  const {
    fields: referenceFields,
    append: addReference,
    remove: removeReference,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'references', // unique name for your Field Array
  });

  const LanguagesSpoken = Object.keys(window.LANGUAGES).map((key) => ({
    value: key,
    label: window.LANGUAGES[key],
  }));

  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    specialties?: boolean;
    professional_highlights?: boolean;
    linkedin_link?: boolean;
    cv?: boolean;
    welcome_packet?: boolean;
    npdb_report?: boolean;
    references?: {
      [key: string]: {
        full_name?: boolean;
        email?: boolean;
        recommendation_letter__file?: boolean;
      };
    };
  }>({});
  const { data: provider } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingProfessionalTraits(),
    queryFn: () => ProviderService.providerCurrentCredentialingProfessionalTraitsRetrieve(),
  });

  const activeReferences = useMemo(
    () => provider?.references?.filter((reference) => !reference.terminated_at) || [],
    [provider?.references]
  );

  useEffect(() => {
    if (!provider) return; // Add this guard clause

    const formData = {
      ...provider,
      references:
        activeReferences?.map((reference) => ({
          ...omit(reference, 'recommendation_letter'),
          recommendation_letter__file: reference.recommendation_letter?.file,
        })) || [], // Ensure it's always an array
    };

    reset(formData, {
      keepDirty: false, // Explicitly reset dirty state
      keepTouched: false, // Reset touched state too
    });
  }, [provider, activeReferences, reset]); // Add reset to dependencies

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingReferencesUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingProfessionalTraits) => {
      await ProviderService.providerCurrentCredentialingProfessionalTraitsUpdate({
        requestBody: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail('current'),
      });
    },
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const onSubmit = async (data) => {
    try {
      const activeReferences = data?.references?.filter((reference) => !reference.terminated_at);
      await mutationProviderCurrentCredentialingReferencesUpdate.mutateAsync({
        ...omit(data, 'references'),
        references: activeReferences?.map(
          (reference): ProviderReference => ({
            ...omit(reference, 'recommendation_letter__file'),
            recommendation_letter: {
              file:
                reference.recommendation_letter__file === ''
                  ? null
                  : reference.recommendation_letter__file,
            },
          })
        ),
      } as ProviderCredentialingProfessionalTraits);
      toast.success('Provider References Updated Successfully');
      onSuccess?.();
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  const degreeOptions = Object.entries(DegreeEnum).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <Card className="p-4">
      <h2 className="border-bottom pb-2 mb-4">Professional Traits</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <FormTextField
          name="npi"
          label="NPI"
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          visibility={FormFieldVisibility.CONNECTED}
          required
          maxLength={10}
        />
        <Form.Group className="mb-3" controlId="degree">
          <FormLabel required visibility={FormFieldVisibility.CONNECTED}>
            Degree(s)
          </FormLabel>
          <SelectController
            name="degree"
            control={control}
            options={degreeOptions}
            isClearable={false}
            isMulti={true}
            placeholder="Select Degrees"
            isInvalid={!!errors.degree || !!showServerFieldErrors.degree}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, 'degree'));
            }}
          />
          <FormFeedback
            clientError={errors.degree}
            serverErrors={showServerFieldErrors.degree ? serverErrors?.body?.degree : null}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="specialties">
          <FormLabel required visibility={FormFieldVisibility.PUBLIC}>
            Specialties
          </FormLabel>
          <SelectController
            name="specialties"
            control={control}
            options={Specialties}
            isMulti
            isClearable
            placeholder="Select Specialties"
            isInvalid={!!errors.specialties || !!showServerFieldErrors.specialties}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, 'specialties'));
            }}
          />
          <FormFeedback
            clientError={errors.specialties}
            serverErrors={
              showServerFieldErrors.specialties ? serverErrors?.body?.specialties : null
            }
          />
        </Form.Group>
        <FormTextareaField
          name="professional_highlights"
          label="Professional Highlights"
          rows={3}
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          visibility={FormFieldVisibility.PUBLIC}
        />

        <FormTextField
          name="linkedin_link"
          label="LinkedIn Link"
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          visibility={FormFieldVisibility.CONNECTED}
        />

        <Form.Group controlId="cv">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>CV</FormLabel>
          <FileUploader
            name="cv"
            className="mb-3"
            control={control}
            dest="providers_documents"
            isClearable
            isInvalid={!!errors.cv || !!showServerFieldErrors.cv}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, ['cv']));
            }}
            onRemove={() => {
              setValue('cv', null, { shouldValidate: true });
            }}
          />
          <FormFeedback
            clientError={errors.cv}
            serverErrors={showServerFieldErrors.cv ? serverErrors?.body?.cv : null}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>
            References (please add 3)
          </FormLabel>
          <FormFeedback clientError={errors?.references?.root || errors?.references} />
          <ul className="list-unstyled">
            {referenceFields.map((item, index) => (
              <li key={item.id} className="mb-3 border rounded p-3">
                <Row>
                  <Col xs={12}>
                    <div className="d-flex justify-content-end">
                      {activeReferences[index]?.current_status && (
                        <StatusPill
                          status={activeReferences[index]?.current_status?.title}
                          category={activeReferences[index]?.current_status?.category}
                        />
                      )}
                    </div>
                    <Row>
                      <Col xs={12} sm={12} md={6}>
                        <FormTextField
                          name={`references.${index}.full_name`}
                          label="Full Name"
                          serverErrors={serverErrors}
                          showServerFieldErrors={showServerFieldErrors}
                          setShowServerFieldErrors={setShowServerFieldErrors}
                          errors={errors}
                          register={register}
                          required
                        />
                      </Col>
                      <Col xs={12} sm={12} md={6}>
                        <FormTextField
                          name={`references.${index}.email`}
                          label="Email"
                          serverErrors={serverErrors}
                          showServerFieldErrors={showServerFieldErrors}
                          setShowServerFieldErrors={setShowServerFieldErrors}
                          errors={errors}
                          register={register}
                          required
                          disabled={!!activeReferences[index]?.id}
                        />
                      </Col>

                      <Col xs={12}>
                        <Form.Group controlId={`references.${index}.recommendation_letter__file`}>
                          <Form.Label className="text-field-label">Reference Letter</Form.Label>
                          <FileUploader
                            name={`references.${index}.recommendation_letter__file`}
                            control={control}
                            className="mb-3"
                            dest="providers_documents"
                            isClearable
                            isInvalid={
                              !!errors.references?.[index]?.file ||
                              !!showServerFieldErrors.references?.[index.toString()]
                                ?.recommendation_letter__file
                            }
                          />
                          <FormFeedback
                            clientError={errors.references?.[index]?.file}
                            serverErrors={serverErrors?.body.references?.[index]?.file}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={12} className="d-flex justify-content-end">
                    <Button
                      className="text-danger"
                      type="button"
                      variant="link"
                      size="lg"
                      onClick={() => removeReference(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            type="button"
            variant="outline-primary"
            onClick={() =>
              addReference({
                full_name: '',
                email: '',
                recommendation_letter__file: null,
              })
            }
          >
            <BsPlus className="me-1" /> Add Reference
          </Button>
        </Form.Group>
        <Form.Group className="mb-3" controlId="languages_spoken">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Languages Spoken</FormLabel>
          <SelectController
            name="languages_spoken"
            control={control}
            options={LanguagesSpoken}
            isMulti
            isClearable
            placeholder="Select Languages"
            isInvalid={!!errors.languages_spoken || !!showServerFieldErrors.languages_spoken}
            onChange={() => {
              setShowServerFieldErrors(omit(showServerFieldErrors, 'languages_spoken'));
            }}
          />
          <FormFeedback
            clientError={errors.languages_spoken}
            serverErrors={
              showServerFieldErrors.languages_spoken ? serverErrors?.body?.languages_spoken : null
            }
          />
        </Form.Group>
        <Row>
          <Col md={12}>
            <Button
              className="float-end"
              variant="primary"
              type="submit"
              disabled={
                mutationProviderCurrentCredentialingReferencesUpdate.isLoading || !isSubmitEnabled
              }
              onClick={() => handleSubmitClick()}
            >
              Save Changes
            </Button>
          </Col>
        </Row>
        <ServerErrorsAlert
          errors={serverErrors}
          showErrors={showGlobalServerErrors}
          clearErrors={handleClearErrorsAlert}
        />
      </Form>
      <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !isSubmitSuccessful} />
    </Card>
  );
};

export const ProviderProfessionalTraits = () => {
  return (
    <ProviderFormContainer>
      <ProviderProfessionalTraitsForm />
    </ProviderFormContainer>
  );
};
